<!-- eslint-disable no-unused-vars -->
<script setup>
    // Imports
    import { db } from '@/firebase';
    import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
    import { ref, onMounted, computed } from 'vue';
    import { useStore } from 'vuex';
    import { useRoute, useRouter } from 'vue-router';
    import Device from '@/api/device';
    import Class from '@/api/class';
    import DefaultLayout from '@/layouts/Layout.vue';
    import UpdateDevice from '@/components/modals/updateDevice.vue';
    import ActionsRequest from '@/components/forms/ActionsRequest.vue';
    import ActionsRequestV2 from '@/components/forms/ActionsRequestV2.vue';
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import DataControlPanel from '@/components/tables/DataControlPanel.vue';
    import AlertDetails from '@/components/common/AlertDetails.vue';
    import { hasPermission } from '@/tools/PermissionChecker';
    import { useModalBackdropClose } from '@/composables/useModalBackdropClose';
    import { isCloudAppPortalUnit, cloudAppPortalPath, unitCloudAppValues } from '@/tools/cloudAppUnit';
    import { useActionStream } from '@/composables/useActionStream';
    import AppManagement from '@/components/management/AppManagement.vue';
    import ChargerCommandPanel from '@/components/forms/ChargerCommandPanel.vue';

    // Reactive references
    const store = useStore();
    const image = ref('');
    const friendlyName = ref('');
    const description = ref('');
    const notes = computed(() => store.state.device?.notes ?? '');
    const quickMetrics = ref([]);
    const classQuickMetrics = ref([]);
    const className = ref('');
    const unitDetails = ref({});
    const modal = ref(null);
    const showNotesModal = ref(false);
    const layoutKey = ref(0);
    const actionVersion = ref(1);
    const activeTab = ref('overview');

    const visibleTabs = computed(() => {
        const tabs = [{ id: 'overview', label: 'Overview' }];
        const cloudAppValues = unitCloudAppValues(store.state.device);
        const hasCloudApp = cloudAppValues.length > 0;
        const isOcppUnit = cloudAppValues.includes('ocpp');
        if (user.role === 'admin' && !hasCloudApp) tabs.push({ id: 'actions', label: 'Actions' });
        if (user.role === 'admin' && !hasCloudApp) tabs.push({ id: 'apps', label: 'Apps' });
        if (user.role === 'admin' && isOcppUnit) tabs.push({ id: 'charger', label: 'Commands' });
        tabs.push({ id: 'alerts', label: 'Alerts' });
        return tabs;
    });

    const closeNotesModal = () => { showNotesModal.value = false };
    const notesModalBackdrop = useModalBackdropClose(closeNotesModal);

    const route = useRoute();
    const router = useRouter();
    const dataID = route.params.dataID;
    const timeFormat = store.state.user.timeFormat;
    const user = store.state.user;

    const { actionHistory } = useActionStream(dataID, {
        timeFormat,
        enabled: !unitCloudAppValues(store.state.device).includes('ocpp'),
    });

    // Permission checks
    const canPauseSource = computed(() => hasPermission('pauseSource'));
    const canEditUnit = computed(() => hasPermission('editUnit'));

    // Lifecycle hook
    onMounted(async () => {
        let dev = store.state.device;
        try {
            const response = await Device.get(`?dataID=${dataID}`);
            dev = response?.details || null;
            if (dev) {
                dev.dataID = dataID;
                store.dispatch('setDevice', dev);
            }
        } catch (e) {
            console.error('Failed to load device for management page:', e);
            // Keep stale store data only if it matches this route.
            if (!dev || dev.dataID !== dataID) {
                dev = null;
            }
        }
        if (dev && isCloudAppPortalUnit(dev)) {
            await router.replace(cloudAppPortalPath(dataID));
            return;
        }

        handleDeviceDetails();
        await loadUnitClassQuickMetrics();

        // Set action form version from device announce (V2 only if action_api_version === 2)
        try {
            const announceCollection = collection(db, 'data', dataID, 'announce');
            const q = query(announceCollection, orderBy('messageUTC', 'desc'), limit(1));
            const querySnapshot = await getDocs(q);
            const doc = querySnapshot.docs[0]?.data();
            actionVersion.value = doc?.action_api_version === 2 ? 2 : 1;
        } catch (e) {
            actionVersion.value = 1;
        }
    });

    // Methods
    const flattenTimeseriesStyleSpecs = (specifications) => {
        if (!specifications || typeof specifications !== 'object' || Array.isArray(specifications)) return [];
        const flattened = [];
        for (const [source, lines] of Object.entries(specifications)) {
            if (!Array.isArray(lines)) continue;
            for (const line of lines) {
                if (!line?.field) continue;
                flattened.push({
                    source,
                    field: line.field,
                    friendlyName: line.friendlyName || line.label || '',
                    unit: line.unit || '',
                });
            }
        }
        return flattened;
    };

    const normalizeQuickMetricSpecs = (rawSpecs) => {
        if (!Array.isArray(rawSpecs)) {
            if (!rawSpecs || typeof rawSpecs !== 'object') return [];
            return Object.keys(rawSpecs)
                .filter((key) => key.includes('.'))
                .map((key) => {
                    const splitIdx = key.indexOf('.');
                    const source = key.slice(0, splitIdx).trim();
                    const field = key.slice(splitIdx + 1).trim();
                    const snapshotValue = rawSpecs[key];
                    return {
                        source,
                        field,
                        friendlyName: (typeof snapshotValue?.friendlyName === 'string' && snapshotValue.friendlyName.trim())
                            ? snapshotValue.friendlyName
                            : field,
                        unit: typeof snapshotValue?.unit === 'string' ? snapshotValue.unit : '',
                        mapAnimationRule: (snapshotValue?.mapAnimationRule && typeof snapshotValue.mapAnimationRule === 'object' && !Array.isArray(snapshotValue.mapAnimationRule))
                            ? { ...snapshotValue.mapAnimationRule }
                            : null,
                    };
                })
                .filter((spec) => spec.source && spec.field);
        }
        return rawSpecs
            .map((spec) => ({
                source: typeof spec === 'string'
                    ? (spec.includes('.') ? spec.slice(0, spec.indexOf('.')).trim() : '')
                    : (typeof spec?.source === 'string' ? spec.source : ''),
                field: typeof spec === 'string'
                    ? (spec.includes('.') ? spec.slice(spec.indexOf('.') + 1).trim() : '')
                    : (typeof spec?.field === 'string' ? spec.field : ''),
                friendlyName: typeof spec?.friendlyName === 'string'
                    ? spec.friendlyName
                    : (typeof spec?.label === 'string' ? spec.label : ''),
                unit: typeof spec?.unit === 'string' ? spec.unit : '',
                mapAnimationRule: (spec?.mapAnimationRule && typeof spec.mapAnimationRule === 'object' && !Array.isArray(spec.mapAnimationRule))
                    ? { ...spec.mapAnimationRule }
                    : null,
            }))
            .filter((spec) => spec.field);
    };

    const loadUnitClassQuickMetrics = async () => {
        classQuickMetrics.value = [];
        className.value = '';
        try {
            const response = await Class.get();
            if (response.status !== 200) return;

            const classesData = await response.json();
            const loaded = Array.isArray(classesData)
                ? classesData
                : (classesData?.classes ?? classesData?.details ?? []);

            const matchedClass = loaded.find((cls) => {
                const members = cls?.unitIds ?? cls?.devices ?? [];
                return Array.isArray(members) && members.includes(dataID);
            });
            if (!matchedClass) return;

            className.value = matchedClass.name || '';
            const rawMetrics =
                matchedClass.quickMetrics ||
                matchedClass.quickMetricSpecs ||
                matchedClass.dashboard?.quickMetrics ||
                flattenTimeseriesStyleSpecs(matchedClass.quickMetricSpecifications || matchedClass.specifications);
            classQuickMetrics.value = normalizeQuickMetricSpecs(rawMetrics);
        } catch (error) {
            console.error('Error loading class quick metrics for unit:', error);
        }
    };

    const handleDeviceDetails = () => {
        try {
            const { 
                friendlyName: extractedName, 
                description: extractedDesc, 
                notes: extractedNotes,
                quickMetrics: extractedQuickMetrics,
                quickMetricSpecs: extractedQuickMetricSpecs,
                image: extractedImage,
                lastMessage,
                currentLon,
                currentLat,
                active,
                entities,
                size,
                lastActivityUTC,
                customTemplate,
                defaultTemplate,
                dataID,
                service,
                Apps,
                ...rest } = store.state.device;

            friendlyName.value = extractedName;
            description.value = extractedDesc;
            // notes is a computed from store.state.device.notes — no assignment needed
            const normalizedQuickMetrics = normalizeQuickMetricSpecs(extractedQuickMetrics);
            const normalizedQuickMetricSpecs = normalizeQuickMetricSpecs(extractedQuickMetricSpecs);
            quickMetrics.value = normalizedQuickMetrics.length > 0
              ? normalizedQuickMetrics
              : normalizedQuickMetricSpecs;
            image.value = extractedImage;
            unitDetails.value = rest;
        } catch (error) {
            console.error('Error loading device details:', error);
        }
    };

    const formatDetailValue = (value) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const entries = Object.values(value);
            if (entries.length > 0 && entries.every((e) => e && typeof e === 'object' && typeof e.friendlyName === 'string')) {
                return entries.map((e) => e.unit ? `${e.friendlyName} (${e.unit})` : e.friendlyName).join(', ');
            }
        }
        return String(value);
    };

    const updateDevice = async ($event) => {
        // Create the form data
        const formData = new FormData();
        formData.append('dataID', dataID);
        formData.append('friendlyName', $event.friendlyName);
        formData.append('description', $event.description);
        formData.append('notes', $event.notes);
        formData.append('quickMetrics', JSON.stringify($event.quickMetrics || []));
        if ($event.image) formData.append('image', $event.image);

        // Update the device
        try {
            const response = await Device.put('', formData);

            if (response.status !== 'success') {
                alert('Failed to update device');
                modal.value = null;
                return;
            }

            // Update the device details and store them.
            const updatedDevice = store.state.device;
            updatedDevice.image = response.details.image;
            updatedDevice.friendlyName = response?.details?.friendlyName ?? $event.friendlyName;
            updatedDevice.description = response?.details?.description ?? $event.description;
            if (typeof response?.details?.notes !== 'undefined') {
                updatedDevice.notes = response.details.notes;
            } else if (typeof $event?.notes !== 'undefined') {
                updatedDevice.notes = $event.notes;
            }
            if (Array.isArray(response?.details?.quickMetrics)) {
                updatedDevice.quickMetrics = response.details.quickMetrics;
            } else if (Array.isArray($event?.quickMetrics)) {
                updatedDevice.quickMetrics = $event.quickMetrics;
            }
            store.dispatch('setDevice', updatedDevice);
            handleDeviceDetails();
            
            modal.value = null;
            layoutKey.value++;
        } catch (error) {
            modal.value = null;
            await new Promise(resolve => setTimeout(resolve, 300));
            alert('Failed to update device');
        }
    };
</script>

<template>
    <DefaultLayout
        :key="layoutKey"
        :dataID="dataID"
    >
        <div class="h-full min-h-0 flex flex-col">

            <!-- ── Tab bar ─────────────────────────────────────────────── -->
            <div class="flex items-center border-b border-line dark:border-line-on-dark bg-mc-cloud dark:bg-mc-charcoal px-4 flex-shrink-0" aria-label="Unit sections">
                <p class="hidden sm:block font-mono text-tier-2 font-medium uppercase tracking-[0.14em] text-ink-muted dark:text-on-dark-muted py-2.5 mr-auto">
                    {{ visibleTabs.find(t => t.id === activeTab)?.label }}
                </p>
                <div class="sm:ml-auto w-full sm:w-auto flex items-center" role="tablist">
                    <button
                        v-for="tab in visibleTabs"
                        :key="tab.id"
                        type="button"
                        role="tab"
                        :id="'tab-' + tab.id"
                        :aria-selected="activeTab === tab.id"
                        :aria-controls="'panel-' + tab.id"
                        class="flex-1 sm:flex-none px-4 py-2.5 font-body font-semibold text-sm text-center sm:text-left border-b-2 -mb-px transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                        :class="activeTab === tab.id
                            ? 'border-mc-red text-mc-ink dark:text-on-dark'
                            : 'border-transparent text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark'"
                        @click="activeTab = tab.id"
                    >
                        {{ tab.label }}
                    </button>
                </div>
            </div>

            <!-- ── Tab content ─────────────────────────────────────────── -->
            <div class="flex-1 min-h-0 overflow-y-auto p-4">

                <!-- OVERVIEW ─────────────────────────────────────────── -->
                <div v-if="activeTab === 'overview'" id="panel-overview" role="tabpanel" aria-labelledby="tab-overview" tabindex="0">

                    <div class="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4 mb-4">

                        <!-- Device image / no-image placeholder + notes -->
                        <div class="flex flex-col gap-2">
                            <div v-if="image" class="max-w-[180px] sm:max-w-none rounded-mc-md overflow-hidden border border-mc-steel dark:border-line-on-dark aspect-square">
                                <img
                                    :src="image"
                                    :alt="(friendlyName || dataID) + ' device image'"
                                    class="w-full h-full object-cover"
                                >
                            </div>
                            <div
                                v-else
                                class="max-w-[180px] sm:max-w-none relative overflow-hidden bg-mc-ink rounded-mc-md aspect-square"
                                style="background-image: radial-gradient(circle, rgba(255,255,255,0.07) 1.5px, transparent 1.5px); background-size: 32px 32px;"
                            >
                                <div class="absolute top-0 left-0 bottom-0 w-1 bg-mc-red" aria-hidden="true"></div>
                                <div class="absolute inset-0 flex items-center justify-center">
                                    <span class="font-mono text-tier-1 uppercase tracking-[0.14em] text-on-dark-muted">No image</span>
                                </div>
                            </div>
                            <button
                                v-if="notes"
                                type="button"
                                class="btn-primary w-full py-1.5 text-xs"
                                @click="showNotesModal = true"
                            >
                                Notes
                            </button>
                        </div>

                        <!-- Name + Description + Unit Details stacked -->
                        <div class="flex flex-col gap-3">
                            <!-- NAME -->
                            <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                                <div class="flex items-center justify-between mb-2">
                                    <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Name</h2>
                                    <button
                                        v-if="canEditUnit"
                                        type="button"
                                        class="p-1.5 rounded-mc-sm text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                                        aria-label="Edit unit"
                                        @click="modal = 'updateDevice'"
                                    >
                                        <font-awesome-icon :icon="['fas', 'edit']" class="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <p class="font-body text-tier-3 text-mc-ink dark:text-on-dark leading-relaxed">{{ friendlyName || dataID }}</p>
                            </div>

                            <!-- DESCRIPTION -->
                            <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                                <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-2">Description</h2>
                                <p v-if="description" class="font-body text-tier-3 text-mc-ink dark:text-on-dark leading-relaxed">{{ description }}</p>
                                <p v-else class="font-body text-tier-3 text-ink-muted dark:text-on-dark-muted">Add a description...</p>
                            </div>

                            <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                                <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-3">Unit Details</h2>
                                <dl class="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                                    <div v-for="(value, key) in unitDetails" :key="key" class="flex flex-col gap-0.5">
                                        <dt class="font-mono text-tier-1 uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted">{{ key }}</dt>
                                        <dd class="font-body font-semibold text-mc-ink dark:text-on-dark text-tier-2 break-all">{{ formatDetailValue(value) }}</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <!-- Data Control Panel (pauseSource permission only) -->
                    <div v-if="canPauseSource" class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md overflow-hidden">
                        <div class="px-4 pt-3 pb-1">
                            <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Data Control</h2>
                        </div>
                        <DataControlPanel :dataID="dataID" />
                    </div>
                </div>

                <!-- ACTIONS (admin only) ──────────────────────────────── -->
                <div v-else-if="activeTab === 'actions'" id="panel-actions" role="tabpanel" aria-labelledby="tab-actions" tabindex="0">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                            <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-3">Send Action Request</h2>
                            <ActionsRequest v-if="actionVersion === 1" :dataID="dataID" />
                            <ActionsRequestV2 v-else :dataID="dataID" />
                        </div>
                        <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                            <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-2">About Actions</h2>
                            <p class="font-body text-tier-3 text-mc-ink dark:text-on-dark leading-relaxed">Actions are commands sent directly to the device. The device acknowledges the request and returns a result when complete.</p>
                            <p class="font-body text-tier-3 text-ink-muted dark:text-on-dark-muted mt-3">
                                API version: <strong class="text-mc-ink dark:text-on-dark">{{ actionVersion }}</strong>
                            </p>
                        </div>
                    </div>

                    <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md overflow-hidden">
                        <div class="px-4 pt-3 pb-1">
                            <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Action Request History</h2>
                        </div>
                        <div class="overflow-x-auto">
                            <GenericTable2
                                :data="actionHistory"
                                :pagination="true"
                            />
                        </div>
                    </div>
                </div>

                <!-- APPS (admin only) ───────────────────────────────── -->
                <div v-else-if="activeTab === 'apps'" id="panel-apps" role="tabpanel" aria-labelledby="tab-apps" tabindex="0">
                    <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                        <AppManagement :deviceId="dataID" />
                    </div>
                </div>

                <!-- CHARGER (admin + ocpp only) ────────────────────── -->
                <div v-else-if="activeTab === 'charger'" id="panel-charger" role="tabpanel" aria-labelledby="tab-charger" tabindex="0">
                    <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                        <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-3">Send Charger Command</h2>
                        <ChargerCommandPanel :dataID="dataID" />
                    </div>
                </div>

                <!-- ALERTS ───────────────────────────────────────────── -->
                <div v-else-if="activeTab === 'alerts'" id="panel-alerts" role="tabpanel" aria-labelledby="tab-alerts" tabindex="0">
                    <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-4">
                        <h2 class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-3">
                            {{ friendlyName || dataID }}'s Alerts
                        </h2>
                        <AlertDetails :dataID="dataID" />
                    </div>
                </div>

            </div>
        </div>
    </DefaultLayout>

    <UpdateDevice v-if="modal === 'updateDevice'"
        :dataID="dataID"
        :friendlyName="friendlyName"
        :description="description"
        :notes="notes"
        :quick-metrics="quickMetrics"
        :snapshots="unitDetails.snapshots || {}"
        :class-quick-metrics="classQuickMetrics"
        :class-name="className"
        @close="modal = null"
        @submitForm="updateDevice"
    />

    <!-- Notes Modal -->
    <div
        v-if="showNotesModal"
        class="fixed top-0 left-0 right-0 bottom-0 bg-mc-ink/30 flex justify-center items-center z-50"
        @mousedown.self="notesModalBackdrop.onBackdropMousedown"
        @mouseup.self="notesModalBackdrop.onBackdropMouseup"
    >
        <div
            class="bg-mc-white dark:bg-mc-charcoal rounded-mc-md shadow-mc-md w-[calc(100%-2rem)] sm:w-[30%] max-h-[60vh] overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notes-modal-title"
            tabindex="-1"
            @mousedown="notesModalBackdrop.onContentMousedown"
            @keydown.esc="showNotesModal = false"
        >
            <div class="flex justify-between items-center p-4 border-b border-mc-steel dark:border-line-on-dark">
                <h3 id="notes-modal-title" class="font-display font-black text-tier-4 text-mc-ink dark:text-on-dark">Notes</h3>
                <button
                    @click="showNotesModal = false"
                    class="text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark transition-colors duration-160 text-2xl leading-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    aria-label="Close"
                >
                    ×
                </button>
            </div>
            <div class="p-4 overflow-y-auto flex-1">
                <p v-if="notes" class="font-body text-tier-3 text-mc-ink dark:text-on-dark whitespace-pre-wrap">{{ notes }}</p>
                <p v-else class="font-body text-tier-3 text-ink-muted dark:text-on-dark-muted italic">No notes added yet. Click edit to add notes.</p>
            </div>
        </div>
    </div>
</template>
